import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';

const GpsPointPickerField = ({
    initialLatitude = '',
    initialLongitude = '',
    disabled = false,
    initialGpsEnabled = false,
    pickerUrl = '',
}) => {
    const [gpsEnabled, setGpsEnabled] = useState(!!initialGpsEnabled);
    const [latitude, setLatitude] = useState(initialLatitude === null || initialLatitude === '' ? '' : String(initialLatitude));
    const [longitude, setLongitude] = useState(initialLongitude === null || initialLongitude === '' ? '' : String(initialLongitude));

    useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }

        const gpsCheckbox = document.querySelector('input[name="gps"]');
        if (!gpsCheckbox) {
            return undefined;
        }

        const syncCheckboxState = () => {
            setGpsEnabled(!!gpsCheckbox.checked);
        };

        syncCheckboxState();
        gpsCheckbox.addEventListener('change', syncCheckboxState);

        return () => {
            gpsCheckbox.removeEventListener('change', syncCheckboxState);
        };
    }, []);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            const payload = event.data || {};
            if (payload.type !== 'aiv-gps-picker:selected') {
                return;
            }

            if (typeof payload.latitude === 'string') {
                setLatitude(payload.latitude);
            }
            if (typeof payload.longitude === 'string') {
                setLongitude(payload.longitude);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const resolvedPickerUrl = useMemo(() => {
        if (!pickerUrl) {
            return '';
        }

        const url = new URL(pickerUrl, window.location.origin);
        if (latitude) {
            url.searchParams.set('lat', latitude);
        }
        if (longitude) {
            url.searchParams.set('lng', longitude);
        }
        return url.toString();
    }, [latitude, longitude, pickerUrl]);

    const openPickerWindow = () => {
        if (!resolvedPickerUrl) {
            return;
        }

        const popup = window.open(
            resolvedPickerUrl,
            'aiv-gps-picker',
            'popup=yes,width=1400,height=900,resizable=yes,scrollbars=yes'
        );

        if (!popup) {
            window.alert('Unable to open the GPS picker window. Please allow popups for this site.');
            return;
        }

        popup.focus();
    };

    return (
        <Box sx={{ mt: 1, mb: 2 }}>
            <div className="row">
                <div className="col-sm-4">
                    <div className="form-group mb-0">
                        <input
                            className="form-control"
                            name="gps_latitude"
                            type="number"
                            step="0.0000001"
                            min="-90"
                            max="90"
                            placeholder="Latitude"
                            value={latitude}
                            onChange={(event) => setLatitude(event.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </div>
                <div className="col-sm-4">
                    <div className="form-group mb-0">
                        <input
                            className="form-control"
                            name="gps_longitude"
                            type="number"
                            step="0.0000001"
                            min="-180"
                            max="180"
                            placeholder="Longitude"
                            value={longitude}
                            onChange={(event) => setLongitude(event.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </div>
                <div className="col-sm-3">
                    <Button
                        variant="outlined"
                        startIcon={<PlaceIcon />}
                        onClick={openPickerWindow}
                        disabled={disabled}
                        sx={{ minWidth: 170, height: 52 }}>
                        Pick on map
                    </Button>
                </div>
            </div>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {gpsEnabled
                    ? 'GPS is enabled on this flux, so the next position reported by the camera/device will update the live GPS location used in group GPS maps. You can still set a manual point here as a temporary fallback.'
                    : 'You can set a fixed manual GPS point here. If you later enable GPS on the flux, the next coordinates reported by the camera/device will update the live GPS location used in group GPS maps.'}
            </Typography>
        </Box>
    );
};

export default GpsPointPickerField;
